/**
 * DealDrawer.jsx
 * ==============
 * Slide-over drawer for a single deal. Two things:
 *   1. Timeline of every touch (from /api/copilot/deal/:id/timeline).
 *   2. Chat-style input at the bottom with two modes:
 *        'log'    → "Called Cole at 3pm, he wants mockups by Thursday"
 *                   → agent parses, logs, confirms + shows extracted fields.
 *        'reply'  → "Their reply: 'Thanks, we're heads-down on peak season, revisit in Feb'"
 *                   → agent classifies, translates, drafts response.
 *
 * The drawer intentionally does not fetch context on open — that's what
 * /timeline is for. Keeps the drawer fast to open, expensive things (Claude)
 * only fire when the operator hits Send.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

const COLORS = {
  navy:   '#0B1B3A',
  teal:   '#00CBC0',
  pink:   '#FF3489',
  purple: '#743CF7',
  amber:  '#FBBF24',
  red:    '#EF4444',
  gray50: '#F9FAFB',
  gray100:'#F3F4F6',
  gray400:'#9CA3AF',
  gray500:'#6B7280',
  gray700:'#374151',
};

const API_BASE = process.env.REACT_APP_COPILOT_API
  || (typeof window !== 'undefined' && window.location.hostname === 'localhost'
        ? 'http://localhost:8080'
        : '');

function usd(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${Math.round(v / 1_000)}K`;
  return `$${v.toFixed(0)}`;
}

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function touchIcon(type) {
  switch (type) {
    case 'call':                return '☎';
    case 'voicemail':           return '🎤';
    case 'email_sent':          return '✉→';
    case 'email_reply_received':return '↩ ✉';
    case 'sms':                 return '💬';
    case 'whatsapp':            return '💬 W';
    case 'linkedin_message':    return 'in';
    case 'linkedin_connect':    return 'in+';
    case 'meeting':             return '👥';
    case 'note':                return '📝';
    case 'system_notification': return '🔔';
    default:                    return '•';
  }
}

function outcomeColor(outcome) {
  switch (outcome) {
    case 'positive':  return COLORS.teal;
    case 'negative':  return COLORS.red;
    case 'objection': return COLORS.amber;
    case 'no_answer': return COLORS.gray400;
    case 'ghost':     return COLORS.gray400;
    case 'pending':   return COLORS.purple;
    default:          return COLORS.gray500;
  }
}

const CONVO_KEY = (dealId) => `copilot_convo_${dealId}`;

function loadConvoFromStorage(dealId) {
  try {
    const raw = localStorage.getItem(CONVO_KEY(dealId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
function saveConvoToStorage(dealId, convo) {
  try {
    // Keep only the last 30 turns to avoid unbounded growth.
    localStorage.setItem(CONVO_KEY(dealId), JSON.stringify(convo.slice(-30)));
  } catch { /* localStorage full or disabled */ }
}

export default function DealDrawer({ item, viewer, email, onClose, onLogged }) {
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [mode, setMode] = useState('log');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  // Session-persistent per-deal conversation memory
  const [agentResponses, setAgentResponses] = useState(() => loadConvoFromStorage(item.deal_id));
  const [error, setError] = useState(null);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  // Persist convo whenever it changes
  useEffect(() => {
    saveConvoToStorage(item.deal_id, agentResponses);
  }, [agentResponses, item.deal_id]);

  const loadTimeline = useCallback(async () => {
    setTimelineLoading(true);
    try {
      const resp = await fetch(
        `${API_BASE}/api/copilot/deal/${encodeURIComponent(item.deal_id)}/timeline?_t=${Date.now()}`,
        {
          cache: 'no-store',
          headers: { 'X-Copilot-User': viewer, 'X-Copilot-Email': email || '' },
        }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTimeline(data);
    } catch (e) {
      setError('Failed to load timeline: ' + e.message);
    } finally {
      setTimelineLoading(false);
    }
  }, [item.deal_id, viewer]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  useEffect(() => {
    // ESC to close
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    // Autoscroll conversation to bottom when new response arrives
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [agentResponses.length, timeline?.count]);

  const send = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    setError(null);
    const submittedText = trimmed;
    const submittedMode = mode;

    try {
      let resp, body;
      if (submittedMode === 'ask') {
        resp = await fetch(`${API_BASE}/api/copilot/ask`, {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json', 'X-Copilot-User': viewer, 'X-Copilot-Email': email || '' },
          body: JSON.stringify({ deal_id: item.deal_id, question: submittedText }),
        });
      } else {
        resp = await fetch(`${API_BASE}/api/copilot/log-nl`, {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json', 'X-Copilot-User': viewer, 'X-Copilot-Email': email || '' },
          body: JSON.stringify({
            deal_id: item.deal_id,
            mode: submittedMode,   // 'log' | 'reply'
            text: submittedText,
            voice_of: viewer,
          }),
        });
      }
      body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(body.message || `HTTP ${resp.status}`);
        return;
      }

      setAgentResponses((prev) => [...prev, {
        id: Date.now(),
        submitted_text: submittedText,
        submitted_mode: submittedMode,
        response: body,
      }]);
      setText('');
      // Refresh timeline so any new touch appears (ask mode does not create one)
      if (submittedMode !== 'ask') {
        loadTimeline();
        if (onLogged) onLogged();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
      if (inputRef.current) inputRef.current.focus();
    }
  }, [text, mode, item.deal_id, viewer, loadTimeline, onLogged]);

  const clearConvo = useCallback(() => {
    if (agentResponses.length === 0) return;
    if (window.confirm('Clear the conversation for this deal?')) {
      setAgentResponses([]);
      saveConvoToStorage(item.deal_id, []);
    }
  }, [agentResponses.length, item.deal_id]);

  const cleanPhone = String(item.contact_phone || '').replace(/[^\d+]/g, '');

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(11,27,58,0.35)',
        backdropFilter: 'blur(4px)',
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div style={{
        width: 'min(680px, 92vw)',
        height: '100vh',
        background: '#fff',
        boxShadow: '-10px 0 40px -12px rgba(11,27,58,0.3)',
        display: 'flex', flexDirection: 'column',
        fontFamily: "'Inter', -apple-system, sans-serif",
        color: COLORS.navy,
      }}>

        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid rgba(11,27,58,0.06)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em', color: COLORS.navy }}>
              {item.company_name || '?'}
              <span style={{ marginLeft: 10, fontSize: 14, fontWeight: 500, color: COLORS.gray400 }}>
                {usd(item.arr_usd)}
              </span>
            </div>
            <div style={{ fontSize: 13, color: COLORS.gray500, marginTop: 4 }}>
              {item.contact_name}{item.contact_title ? ' · ' + item.contact_title : ''}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {cleanPhone && <a href={`tel:${cleanPhone}`} style={pillLink}>☎ {item.contact_phone}</a>}
              {item.contact_email && <a href={`mailto:${item.contact_email}`} style={pillLink}>✉ {item.contact_email}</a>}
              {item.hubspot_url && <a href={item.hubspot_url} target="_blank" rel="noreferrer" style={pillLink}>◉ HubSpot</a>}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none',
            fontSize: 22, color: COLORS.gray400, cursor: 'pointer',
            padding: '0 4px', lineHeight: 1,
          }}>×</button>
        </div>

        {/* Timeline + agent conversation (scrollable) */}
        <div ref={scrollerRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', background: COLORS.gray50 }}>
          {timelineLoading && (
            <div style={{ padding: 20, textAlign: 'center', color: COLORS.gray500, fontSize: 13 }}>
              Loading timeline...
            </div>
          )}

          {timeline && timeline.count === 0 && agentResponses.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: COLORS.gray500, fontSize: 13 }}>
              No touches logged yet. Use the input below to describe what you did.
            </div>
          )}

          {/* Timeline (newest first) */}
          {timeline && timeline.items.map((t) => (
            <TimelineItem key={t.touch_id} touch={t} />
          ))}

          {/* Ephemeral agent responses appear inline below the timeline */}
          {agentResponses.map((r) => (
            <AgentResponse key={r.id} entry={r} viewer={viewer} />
          ))}
        </div>

        {/* Composer */}
        <div style={{
          borderTop: '1px solid rgba(11,27,58,0.06)',
          padding: '16px 24px 20px',
          background: '#fff',
        }}>
          {error && (
            <div style={{
              padding: '8px 12px', marginBottom: 10, borderRadius: 8,
              background: 'rgba(239,68,68,0.08)', color: COLORS.red, fontSize: 12,
              border: '1px solid rgba(239,68,68,0.2)',
            }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <ModeButton active={mode === 'log'} onClick={() => setMode('log')}>
                I did...
              </ModeButton>
              <ModeButton active={mode === 'reply'} onClick={() => setMode('reply')}>
                They replied...
              </ModeButton>
              <ModeButton active={mode === 'ask'} onClick={() => setMode('ask')}>
                Ask...
              </ModeButton>
            </div>
            {agentResponses.length > 0 && (
              <button onClick={clearConvo} style={{
                padding: '4px 10px', fontSize: 10, background: 'transparent',
                border: '1px solid rgba(11,27,58,0.1)', borderRadius: 6,
                color: COLORS.gray400, cursor: 'pointer',
                fontFamily: "'JetBrains Mono', monospace",
              }}>clear ×</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={
                mode === 'log'
                  ? "e.g. Called Cole at 3pm, he wants wallet UI mockups before Thursday's technical review with Karim"
                  : mode === 'reply'
                  ? "Paste the reply you received. I'll classify it, translate what they mean, and draft a response."
                  : "Ask anything about this deal. e.g. 'why did they ghost?' or 'what did we discuss last time?' or 'who's the real decision-maker?'"
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  send();
                }
              }}
              disabled={sending}
              style={{
                flex: 1,
                minHeight: 60, maxHeight: 200,
                padding: '10px 14px',
                fontSize: 13, lineHeight: 1.5,
                fontFamily: "'Inter', sans-serif",
                border: '1.5px solid rgba(11,27,58,0.12)',
                borderRadius: 12,
                color: COLORS.navy,
                background: COLORS.gray50,
                resize: 'vertical',
                outline: 'none',
              }}
            />
            <button
              onClick={send}
              disabled={sending || !text.trim()}
              style={{
                padding: '10px 18px',
                background: sending || !text.trim() ? COLORS.gray400 : COLORS.navy,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 13, fontWeight: 600,
                cursor: sending ? 'wait' : 'pointer',
                fontFamily: "'Inter', sans-serif",
                boxShadow: '0 4px 12px -4px rgba(11,27,58,0.3)',
                whiteSpace: 'nowrap',
              }}
            >
              {sending ? '…' : 'Send  ⏎'}
            </button>
          </div>
          <div style={{ fontSize: 10, color: COLORS.gray400, marginTop: 6, fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.04em' }}>
            {mode === 'log'
              ? 'Agent will extract touch type, outcome, next action, meetings — then save to your log and HubSpot.'
              : mode === 'reply'
              ? 'Agent will classify the reply, tell you what they really mean, and draft a response in your voice.'
              : 'Agent will answer using the deal context (HubSpot + Fathom + your prior touches). No writes.'}
            {' · '}Cmd/Ctrl+Enter to send
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function TimelineItem({ touch }) {
  const oc = outcomeColor(touch.outcome);
  const inbound = touch.direction === 'inbound';
  return (
    <div style={{
      marginBottom: 10,
      padding: '10px 14px',
      background: '#fff',
      borderRadius: 10,
      border: '1px solid rgba(11,27,58,0.06)',
      borderLeft: `3px solid ${oc}`,
      display: 'grid', gridTemplateColumns: '36px 1fr auto', gap: 10,
    }}>
      <div style={{
        width: 32, height: 32,
        borderRadius: 8,
        background: `${oc}18`,
        color: oc,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 700,
      }}>{touchIcon(touch.touch_type)}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.navy }}>
          {touch.touch_type.replace(/_/g, ' ')}
          {inbound && <span style={{ marginLeft: 6, fontSize: 10, color: COLORS.pink, fontFamily: "'JetBrains Mono',monospace" }}>INBOUND</span>}
          {touch.outcome && (
            <span style={{
              marginLeft: 8, padding: '1px 8px',
              background: `${oc}15`, color: oc,
              borderRadius: 6, fontSize: 10, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              fontFamily: "'JetBrains Mono',monospace",
            }}>
              {touch.outcome}
            </span>
          )}
        </div>
        {touch.body_summary && (
          <div style={{ fontSize: 12, color: COLORS.gray700, marginTop: 4, lineHeight: 1.4 }}>
            {touch.body_summary}
          </div>
        )}
        {touch.next_action && (
          <div style={{
            marginTop: 6, padding: '4px 8px',
            background: 'rgba(0,203,192,0.08)', color: COLORS.teal,
            borderRadius: 6, fontSize: 11, display: 'inline-block',
            fontFamily: "'JetBrains Mono',monospace",
          }}>
            → {touch.next_action.type}{touch.next_action.due ? ` · due ${touch.next_action.due}` : ''}
          </div>
        )}
        {Array.isArray(touch.intent_labels) && touch.intent_labels.length > 0 && (
          <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {touch.intent_labels.slice(0, 4).map((tag) => (
              <span key={tag} style={{
                padding: '2px 6px', fontSize: 10, background: COLORS.gray100, color: COLORS.gray700,
                borderRadius: 4, fontFamily: "'JetBrains Mono',monospace",
              }}>{tag}</span>
            ))}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', fontSize: 10, color: COLORS.gray400, fontFamily: "'JetBrains Mono',monospace" }}>
        <div>{timeAgo(touch.touch_at)}</div>
        <div style={{ marginTop: 2, opacity: 0.7 }}>by {touch.actor || '—'}</div>
      </div>
    </div>
  );
}

function AgentResponse({ entry }) {
  const r = entry.response;
  const mode = entry.submitted_mode;

  const bubbleLabel = mode === 'reply' ? 'They replied'
                    : mode === 'ask'   ? 'You asked'
                    : 'I did';

  return (
    <div style={{ marginBottom: 14 }}>
      {/* User bubble */}
      <div style={{
        marginBottom: 6,
        padding: '10px 14px',
        background: COLORS.navy,
        color: '#fff',
        borderRadius: 12,
        borderBottomRightRadius: 4,
        fontSize: 13,
        maxWidth: '80%',
        marginLeft: 'auto',
        whiteSpace: 'pre-wrap',
      }}>
        <div style={{
          fontSize: 10, color: 'rgba(255,255,255,0.6)',
          marginBottom: 4, fontFamily: "'JetBrains Mono',monospace",
          letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>
          {bubbleLabel}
        </div>
        {entry.submitted_text}
      </div>

      {/* Agent response */}
      <div style={{
        padding: '12px 14px',
        background: 'linear-gradient(135deg, rgba(0,203,192,0.08), rgba(116,60,247,0.06))',
        border: '1px solid rgba(0,203,192,0.2)',
        borderRadius: 12,
        borderTopLeftRadius: 4,
        maxWidth: '90%',
        fontSize: 13,
      }}>
        <div style={{
          fontSize: 10, color: COLORS.teal, marginBottom: 6,
          fontFamily: "'JetBrains Mono',monospace",
          letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 700,
        }}>
          ✨ Copilot
        </div>
        {mode === 'reply' && <ReplyCoachBlock data={r} />}
        {mode === 'log'   && <ExtractedBlock data={r.extracted} />}
        {mode === 'ask'   && <AskBlock data={r} />}
      </div>
    </div>
  );
}

function AskBlock({ data }) {
  if (!data) return null;
  return (
    <div>
      <div style={{ fontSize: 13, color: COLORS.navy, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
        {data.answer}
      </div>
      {typeof data.confidence === 'number' && (
        <div style={{
          fontSize: 10, color: COLORS.gray400, marginTop: 8,
          fontFamily: "'JetBrains Mono',monospace",
        }}>
          confidence: {(data.confidence * 100).toFixed(0)}%
          {Array.isArray(data.sources) && data.sources.length > 0 && (
            <> · sources: {data.sources.slice(0, 3).join(', ')}</>
          )}
        </div>
      )}
      {data.suggested_follow_up && (
        <div style={{
          marginTop: 8, padding: '6px 10px', background: 'rgba(0,203,192,0.08)',
          borderRadius: 6, fontSize: 11, color: COLORS.teal,
        }}>
          → {data.suggested_follow_up}
        </div>
      )}
    </div>
  );
}

function ExtractedBlock({ data }) {
  if (!data) return <div style={{ color: COLORS.gray500 }}>Could not parse.</div>;
  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        <strong>Logged as:</strong> {data.touch_type}
        {data.outcome && (
          <span style={{
            marginLeft: 8, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
            background: `${outcomeColor(data.outcome)}20`, color: outcomeColor(data.outcome),
          }}>{data.outcome}</span>
        )}
      </div>
      {data.body_summary && (
        <div style={{ fontSize: 12, color: COLORS.gray700, marginBottom: 6 }}>
          {data.body_summary}
        </div>
      )}
      {data.next_action_type && (
        <div style={{
          padding: '6px 10px', background: 'rgba(0,203,192,0.12)', color: COLORS.teal,
          borderRadius: 6, fontSize: 12, display: 'inline-block', marginTop: 4,
          fontFamily: "'JetBrains Mono',monospace", fontWeight: 600,
        }}>
          Next: {data.next_action_type}{data.next_action_due ? ` · ${data.next_action_due}` : ''}
        </div>
      )}
      {data.book_meeting_flag && (
        <div style={{ marginTop: 6, fontSize: 12, color: COLORS.pink }}>
          📅 Meeting flagged{data.meeting_scheduled_at ? `: ${new Date(data.meeting_scheduled_at).toLocaleString()}` : ''}
        </div>
      )}
    </div>
  );
}

function ReplyCoachBlock({ data }) {
  if (!data) return null;
  const draft = data.suggested_draft || {};
  const [copied, setCopied] = React.useState(false);
  const copy = (t) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(t);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <span style={{
          padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
          background: `${outcomeColor(data.classification === 'interested' ? 'positive' : data.classification === 'dead' || data.classification === 'not_a_fit' ? 'negative' : data.classification === 'objection' ? 'objection' : 'pending')}20`,
          color: outcomeColor(data.classification === 'interested' ? 'positive' : data.classification === 'dead' || data.classification === 'not_a_fit' ? 'negative' : data.classification === 'objection' ? 'objection' : 'pending'),
          textTransform: 'uppercase', letterSpacing: '0.05em',
          fontFamily: "'JetBrains Mono',monospace",
        }}>{data.classification}</span>
        {' · '}
        <span style={{ fontSize: 12, color: COLORS.navy, fontWeight: 500 }}>{data.recommendation}</span>
      </div>
      <div style={{ fontSize: 12, color: COLORS.gray700, marginBottom: 8, lineHeight: 1.5 }}>
        <strong>What they really mean:</strong> {data.underlying_message}
      </div>
      {data.recommendation_reason && (
        <div style={{ fontSize: 11, color: COLORS.gray500, marginBottom: 8, fontStyle: 'italic' }}>
          {data.recommendation_reason}
        </div>
      )}
      {draft.body && (
        <div style={{
          marginTop: 8, padding: 10,
          background: '#fff', borderRadius: 8, border: '1px solid rgba(11,27,58,0.08)',
        }}>
          <div style={{
            fontSize: 10, color: COLORS.gray500, marginBottom: 6,
            fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.05em', textTransform: 'uppercase',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span>Suggested {draft.channel || 'reply'}</span>
            <button onClick={() => copy(draft.body)} style={{
              padding: '2px 8px', fontSize: 10, background: COLORS.gray50,
              border: '1px solid rgba(11,27,58,0.1)', borderRadius: 4, cursor: 'pointer',
              color: COLORS.gray700, fontFamily: "'Inter',sans-serif",
            }}>{copied ? '✓ copied' : 'copy'}</button>
          </div>
          {draft.subject && (
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
              Subject: {draft.subject}
            </div>
          )}
          <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', color: COLORS.navy, lineHeight: 1.5 }}>
            {draft.body}
          </div>
        </div>
      )}
      {data.wait_until && (
        <div style={{
          marginTop: 8, fontSize: 11, color: COLORS.purple,
          fontFamily: "'JetBrains Mono',monospace",
        }}>
          ⏳ Wait until {data.wait_until}
        </div>
      )}
      {data.escalation_hint && (
        <div style={{
          marginTop: 8, padding: '6px 10px', background: 'rgba(255,52,137,0.08)',
          color: COLORS.pink, borderRadius: 6, fontSize: 11,
        }}>
          → {data.escalation_hint}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function ModeButton({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px',
      fontSize: 12, fontWeight: 600,
      background: active ? COLORS.navy : 'transparent',
      color: active ? '#fff' : COLORS.gray500,
      border: `1.5px solid ${active ? COLORS.navy : 'rgba(11,27,58,0.15)'}`,
      borderRadius: 999,
      cursor: 'pointer',
      fontFamily: "'Inter', sans-serif",
      transition: 'all 0.15s',
    }}>
      {children}
    </button>
  );
}

const pillLink = {
  padding: '3px 8px',
  background: COLORS.gray50,
  color: COLORS.gray700,
  borderRadius: 6,
  fontSize: 11,
  fontFamily: "'JetBrains Mono', monospace",
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
};
